import { useEffect } from 'react';
import { useAuth } from './useAuth';
import { extractPlatformUser } from '../utils/auth0UserUtils';
import { useFeatureFlag, FeatureFlagKey } from './useFeatureFlags';

/**
 * Minimal type for the Aha! feedback widget global.
 * Only the initialize command is declared.
 */
interface AhaAnalytics {
  (command: string, options: Record<string, unknown>): void;
}

function getAha(): AhaAnalytics | undefined {
  return (window as unknown as { aha?: AhaAnalytics }).aha;
}

const AHA_SCRIPT_ID = 'aha-feedback-snippet';
const AHA_ACCOUNT = 'opengov-roadmap';
const AHA_APPLICATION_ID = '6257213701279324215';

// prettier-ignore
const AHA_SNIPPET =
  `(function(w,d,s,o,f,js,fjs){` +
  `w['aha-widget']=o;w[o]=w[o]||function(){(w[o].q=w[o].q||[]).push(arguments);};` +
  `js=d.createElement(s);fjs=d.getElementsByTagName(s)[0];js.id=o;js.src=f;js.async=1;fjs.parentNode.insertBefore(js,fjs);` +
  `})(window,document,'script','aha','https://cdn.aha.io/assets/feedback/feedback.js');`;

/**
 * Injects the Aha! feedback widget snippet and initializes it with the
 * current user's identity.
 *
 * Must be rendered inside `<AuthGate>` (needs Auth0 session).
 * Gated by the `ahaFeedback` feature flag (Integration + Production).
 */
export function useAhaFeedback(): void {
  const { user } = useAuth();
  const platformUser = extractPlatformUser(user);
  const ahaFeedbackEnabled = useFeatureFlag(FeatureFlagKey.PSP_UI_AHA_FEEDBACK);

  useEffect(() => {
    if (!ahaFeedbackEnabled) {
      console.log('[Aha] Skipped: PSP_UI_AHA_FEEDBACK feature flag is disabled');
      return;
    }
    if (!user || !platformUser?.uuid) {
      console.log('[Aha] Skipped: user or platform UUID not yet available');
      return;
    }
    if (document.getElementById(AHA_SCRIPT_ID)) {
      console.log('[Aha] Skipped: snippet already injected');
      return;
    }

    const script = document.createElement('script');
    script.id = AHA_SCRIPT_ID;
    script.textContent = AHA_SNIPPET;
    document.head.appendChild(script);
    console.log('[Aha] Snippet injected into <head>');

    getAha()?.('initialize', {
      account: AHA_ACCOUNT,
      applicationId: AHA_APPLICATION_ID,
      user: {
        id: platformUser.uuid,
        name: user.name,
        email: user.email,
      },
    });
    console.log('[Aha] initialize() called', {
      uuid: platformUser.uuid,
      name: user.name,
      email: user.email,
      account: AHA_ACCOUNT,
      applicationId: AHA_APPLICATION_ID,
    });
  }, [user, platformUser?.uuid, ahaFeedbackEnabled]);
}
